import React from 'react'
import { CoreExportModuleSchoolUnit, CoreModuleView, ModuleTypeName } from "../../models/CoreModels"
import AlvisImportConfiguration, {warning as AlvisImportWarnings } from "./components/modules/components/Alvis/AlvisImportConfiguration";
import AppleSchoolManagerExportConfiguration from "./components/modules/components/AppleSchoolManagerExport/AppleSchoolManagerExportConfiguration";
import AzureAdGroupExportConfiguration, { warning as AzureAdGroupExportWarnings } from "./components/modules/components/AzureAdGroupExport/AzureAdGroupExportConfiguration";
import EdlevoConfiguration, { warning as EdlevoWarnings } from "./components/modules/components/Edlevo/EdlevoConfiguration";
import EduCloudConfiguration, { warningMessages as EduCloudWarnings } from "./components/modules/components/EduCloud/EduCloudConfiguration";
import EntraIdUserExportConfiguration from "./components/modules/components/EntraIdUser/EntraIdUserExportConfiguration";
import GoogleClassroomExportConfiguration, { warningMessages as GoogleClassroomExportWarnings } from "./components/modules/components/GoogleClassroomExport/GoogleClassroomExportConfiguration";
import GoogleGroupExportConfiguration, { warningMessages as GoogleGroupExportWarnings } from "./components/modules/components/GoogleGroupExport/GoogleGroupExportConfiguration";
import GoogleUserExportConfiguration, { warningMessages as GoogleUserExportWarnings } from "./components/modules/components/GoogleUserExport/GoogleUserExportConfiguration";
import HaldorConfiguration from "./components/modules/components/Haldor/HaldorConfiguration";
import SchoolSoftConfiguration, { warningMessages as SchoolSoftWarnings } from "./components/modules/components/SchoolSoft/SchoolSoftConfiguration";
import ScheduleSyncGoogleConfiguration from "./components/modules/components/ScheduleSyncGoogle/ScheduleSyncGoogleConfiguration";
import SchoolSoftScheduleConfiguration from "./components/modules/components/SchoolSoftSchedule/SchoolSoftScheduleConfiguration";
import SDSv21CsvImportConfiguration from "./components/modules/components/SDSv21CsvImport/SDSv21CsvImportConfiguration";
import Skola24ScheduleConfiguration from "./components/modules/components/Skola24Schedule/Skola24ScheduleConfiguration";
import SS12000ApiConfiguration from "./components/modules/components/SS12000API/SS12000ApiConfiguration";
import PasswordDistributionApiConfiguration from './components/modules/components/PasswordDistributionAPI/PasswordDistributionApiConfiguration';
import LocalAdConfiguration from './components/modules/components/LocalAd/LocalAdConfiguration';
import LocalAdScheduleConfiguration from './components/modules/components/LocalAdSchedule/LocalAdScheduleConfiguration';
import MeitnerExportConfiguration, { warningMessages as MeitnerExportWarnings } from './components/modules/components/MeitnerExport/MeitnerExportConfiguration';
import DnpExportConfiguration, { warningMessages as DnpExportWarnings } from './components/modules/components/DnpExport/DnpExportConfiguration';
import FlowSyncCsvImportConfiguration from './components/modules/components/FlowSyncCsvImport/FlowSyncCsvImportConfiguration';
import OneRosterExportConfiguration, { warningMessages as OneRosterExportWarnings } from './components/modules/components/OneRosterExport/OneRosterExportConfiguration';
import GoogleDeviceImportConfiguration from './components/modules/components/GoogleDeviceImport/GoogleDeviceImportConfiguration';

interface ModuleConfigurationProps {
    originalConfiguration: any 
    disabled: boolean
    active: boolean
    moduleTypeName: ModuleTypeName
    setUpdateConfiguration: (arg: any) => void 
    updatedConfiguration: any 
    updateValidation: (arg: boolean) => void
    updateWarnings: (arg: string[]) => void
    exportSchoolUnits: CoreExportModuleSchoolUnit[]
    moduleId?: string
    setAdditionalAppTokenScopes: (arg: string[]) => void
}

type Configuration = {
    component: React.JSX.Element,
    warning?: (originalConfiguration: any, updatedConfiguration: any) => string[],
}

const ModuleConfiguration = (props: ModuleConfigurationProps) => {

    const config = {
        configuration: props.updatedConfiguration,
        disabled: props.disabled,
        active: props.active,
    }

    const getConfiguration = (): Configuration | null => {
        switch (props.moduleTypeName) {
            case ModuleTypeName.AlvisImport:
                return {
                    component: <AlvisImportConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />,
                    warning: AlvisImportWarnings
                }
            case ModuleTypeName.AppleSchoolManager:
                return {
                    component: <AppleSchoolManagerExportConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />
                }
            case ModuleTypeName.AzureADGroupExport:
                return {
                    component: <AzureAdGroupExportConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />,
                    warning: AzureAdGroupExportWarnings
                }
            case ModuleTypeName.DNPExport:
                return {
                    component: <DnpExportConfiguration
                        {...config}
                        moduleId={props.moduleId}
                        originalConfiguration={props.originalConfiguration}
                        schoolUnits={props.exportSchoolUnits}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />,
                    warning: DnpExportWarnings
                }
            case ModuleTypeName.TietoEducation:
                return {
                    component: <EdlevoConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />,
                    warning: EdlevoWarnings
                }
            case ModuleTypeName.EduCloud:
                return {
                    component: <EduCloudConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />,
                    warning: EduCloudWarnings
                }
            case ModuleTypeName.EntraIdUserExport:
                return {
                    component: <EntraIdUserExportConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />
                }
            case ModuleTypeName.FlowSyncCsvImport:
                return {
                    component: <FlowSyncCsvImportConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />
                }
            case ModuleTypeName.GoogleClassroomExport:
                return {
                    component: <GoogleClassroomExportConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />,
                    warning: GoogleClassroomExportWarnings
                }
            case ModuleTypeName.GoogleDeviceImport:
                return {
                    component: <GoogleDeviceImportConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />
                }
            case ModuleTypeName.GoogleGroupExport:
                return {
                    component: <GoogleGroupExportConfiguration
                        {...config}
                        onValidChange={props.updateValidation}
                        updatePart={(p) => updateConfiguration(p)} 
                    />,
                    warning: GoogleGroupExportWarnings
                }
            case ModuleTypeName.GoogleUserExport:
                return {
                    component: <GoogleUserExportConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />,
                    warning: GoogleUserExportWarnings
                }
            case ModuleTypeName.HaldorExport:
                return {
                    component: <HaldorConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />
                }
            case ModuleTypeName.LocalAD:
                return {
                    component: <LocalAdConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />
                }
            case ModuleTypeName.LocalADSchedule:
                return {
                    component: <LocalAdScheduleConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />
                }
            case ModuleTypeName.OneRosterConfiguration:
                return {
                    component: <OneRosterExportConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                        setAdditionalAppTokenScopes={props.setAdditionalAppTokenScopes}
                    />,
                    warning: OneRosterExportWarnings
                }
            case ModuleTypeName.MeitnerExport:
                return {
                    component: <MeitnerExportConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        exportSchoolUnits={props.exportSchoolUnits}
                        onValidChange={props.updateValidation}
                    />,
                    warning: MeitnerExportWarnings
                }
            case ModuleTypeName.ScheduleSyncGoogle:
                return {
                    component: <ScheduleSyncGoogleConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                    />
                }
            case ModuleTypeName.SchoolSoft:
                return {
                    component: <SchoolSoftConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />,
                    warning: SchoolSoftWarnings
                }
            case ModuleTypeName.SchoolSoftSchedule:
                return {
                    component: <SchoolSoftScheduleConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />
                }
            case ModuleTypeName.SDSv21CsvImport:
                return {
                    component: <SDSv21CsvImportConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />
                }
            case ModuleTypeName.Skola24Schedule:
                return {
                    component: <Skola24ScheduleConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                        onValidChange={props.updateValidation}
                    />
                }
            case ModuleTypeName.SS12000API:
                return {
                    component: <SS12000ApiConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                    />
                }
            case ModuleTypeName.PasswordDistributionAPI:
                return {
                    component: <PasswordDistributionApiConfiguration
                        {...config}
                        updatePart={(p) => updateConfiguration(p)}
                    />
                }
            default: return null;
        }
    }

    const configuration = getConfiguration();

    const update = (updatedConfiguration: CoreModuleView) => {
        if (configuration?.warning) {
            let warnings: string[] = configuration.warning(props.originalConfiguration, updatedConfiguration);
            props.updateWarnings(warnings)
        }
    }

    const updateConfiguration = <T,>(p: Partial<T>) => {
        let mergedConfig = { ...props.updatedConfiguration, ...p }
        props.setUpdateConfiguration(mergedConfig);
        update(mergedConfig);
    }

    if (!configuration) {
        return null;
    }

    return configuration.component;
}

export default ModuleConfiguration;
